/**
 * Measures text in the stencil font (Bungee) so labels can be sized to fit
 * their container identically on iOS, Android and web (react-native-web
 * ignores adjustsFontSizeToFit). Same approach as cardNameFit's Zilla table.
 */

/**
 * Bungee-Regular.ttf advance widths in em (unitsPerEm 1000) for printable
 * ASCII and the few symbols the UI uses. Bungee is all-caps: lowercase letters
 * share the capitals' widths. Regenerate from the font's hmtx table if the
 * font file changes.
 */
const BUNGEE_EM: Record<string, number> = {
  " ": 0.225, "!": 0.424, "\"": 0.646, "#": 0.75, "$": 0.65, "%": 1.031, "&": 0.766, "'": 0.356,
  "(": 0.429, ")": 0.429, "*": 0.718, "+": 0.734, ",": 0.384, "-": 0.42, ".": 0.384, "/": 0.347,
  "0": 0.727, "1": 0.601, "2": 0.639, "3": 0.637, "4": 0.709, "5": 0.647, "6": 0.693, "7": 0.627,
  "8": 0.711, "9": 0.693, ":": 0.384, ";": 0.384, "<": 0.714, "=": 0.734, ">": 0.714, "?": 0.656,
  "@": 0.867, "A": 0.73, "B": 0.725, "C": 0.628, "D": 0.746, "E": 0.654, "F": 0.618, "G": 0.708,
  "H": 0.759, "I": 0.605, "J": 0.688, "K": 0.746, "L": 0.695, "M": 0.849, "N": 0.753, "O": 0.737,
  "P": 0.682, "Q": 0.737, "R": 0.743, "S": 0.65, "T": 0.656, "U": 0.746, "V": 0.73, "W": 0.831,
  "X": 0.737, "Y": 0.705, "Z": 0.66, "[": 0.429, "\\": 0.347, "]": 0.429, "^": 0.714, "_": 0.734,
  "`": 0.5, "a": 0.73, "b": 0.725, "c": 0.628, "d": 0.746, "e": 0.654, "f": 0.618, "g": 0.708,
  "h": 0.759, "i": 0.605, "j": 0.688, "k": 0.746, "l": 0.695, "m": 0.849, "n": 0.753, "o": 0.737,
  "p": 0.682, "q": 0.737, "r": 0.743, "s": 0.65, "t": 0.656, "u": 0.746, "v": 0.73, "w": 0.831,
  "x": 0.737, "y": 0.705, "z": 0.66, "{": 0.455, "|": 0.347, "}": 0.455, "~": 0.734, "·": 0.364,
  "–": 0.734, "…": 1.11, "→": 1.0
}
/** Wider than any glyph in the table, so an unknown character never under-measures. */
const FALLBACK_EM = 1.2
/** Room for kerning and rasterisation differences between platforms. */
const WIDTH_SAFETY = 0.96

/** Width in em, including letter spacing (e.g. 0.1 for tracking-widest). */
export function stencilWidthEm(text: string, letterSpacingEm = 0): number {
  let em = 0
  for (const ch of text) em += (BUNGEE_EM[ch] ?? FALLBACK_EM) + letterSpacingEm
  return em
}

/**
 * Largest font size between `min` and `base` at which `text` fits on one line
 * of `width` px; `min` when it does not fit even then (the caller wraps or
 * ellipsizes). Returns `base` until the container has been measured.
 */
export function fitStencilSize(text: string, width: number, base: number, min: number, letterSpacingEm = 0): number {
  if (!(width > 0) || text.length === 0) return base
  const fitted = (width * WIDTH_SAFETY) / stencilWidthEm(text, letterSpacingEm)
  return Math.max(min, Math.min(base, Math.floor(fitted * 4) / 4))
}
